/**
 * Draw a binned measure onto a pitch.
 *
 * The colour ramp is this module's own, and deliberately NOT the match page's.
 * That one is warm and rises to red because it shades continuous occupancy
 * under live video (founder ask, 2026-08-27, "redder faster"); this one shades
 * discrete counted bins over a static pitch, where the reading has to be
 * "darker means more" at a glance and the markings underneath must survive.
 * Sharing one ramp would mean every tweak to either display silently retunes
 * the other.
 *
 * Nothing here knows what is being shaded. The bin geometry arrives on the
 * result columns (`bin_width`, in the property's own unit) and the pitch
 * extent arrives on the catalog property (`range`), so this module never
 * decides how big a pitch is or how wide a bin is — it is a renderer, not a
 * second opinion about the data.
 */

export interface PitchCell {
  /** Bin lower edges, in the property's unit — as the compiler emits them. */
  x: number
  y: number
  value: number
  /** What to print inside the cell. Omitted, nothing is printed. */
  text?: string
  /** Index of the result row this cell came from, for hit-testing. */
  index?: number
}

/**
 * Which cell is under a pointer, in the canvas's own CSS pixel coordinates.
 *
 * Returned by `draw` rather than exported as a function of its own, because
 * the answer depends on the layout that draw just computed — a second
 * implementation of that arithmetic would be a second thing to keep in step.
 */
export type HitTest = (offsetX: number, offsetY: number) => PitchCell | null

export interface PitchExtent {
  min: number
  max: number
}

export interface PitchOptions {
  cells: PitchCell[]
  binX: number
  binY: number
  /**
   * The playing area to outline, in metres about the centre spot. The caller
   * supplies the match's measured half-extents where it has them; the
   * catalog's nominal 105 x 68 is only the fallback for a result that spans
   * several pitch sizes or none that could be measured.
   */
  pitchX: PitchExtent
  pitchY: PitchExtent
}

/** Where the drawing has to reach: the pitch, plus anything outside it. */
export function extent(o: PitchOptions): { x: PitchExtent; y: PitchExtent } {
  const x = { ...o.pitchX }
  const y = { ...o.pitchY }
  for (const c of o.cells) {
    x.min = Math.min(x.min, c.x)
    x.max = Math.max(x.max, c.x + o.binX)
    y.min = Math.min(y.min, c.y)
    y.max = Math.max(y.max, c.y + o.binY)
  }
  return { x, y }
}

const LINE = 'rgba(255,255,255,0.42)'
const OUTSIDE = 'rgba(255,255,255,0.05)'

/**
 * Pale to deep blue: light means less, dark means more.
 *
 * Two constraints shape it. The markings have to stay readable through every
 * cell, so alpha is capped well short of opaque and the pitch lines are
 * stroked after the fill — a cell can tint a penalty box but never erase it.
 * And the top of the ramp stops at a saturated blue rather than continuing to
 * navy: on this page's near-black surface a nearly-black cell reads as an
 * empty one, which would invert the very thing the ramp is for.
 *
 * Alpha carries magnitude alongside colour, so a busy cell is both darker and
 * more solid, and a nearly-empty one barely disturbs the pitch beneath it.
 */
const PALE: [number, number, number] = [226, 238, 255]
const MID: [number, number, number] = [86, 148, 236]
const DEEP: [number, number, number] = [26, 74, 178]
const MIN_ALPHA = 0.26
const MAX_ALPHA = 0.78

export function ramp(t: number): [number, number, number, number] {
  const v = t <= 0 ? 0 : Math.min(1, t)
  const [from, to, k] = v < 0.5
    ? [PALE, MID, v * 2]
    : [MID, DEEP, (v - 0.5) * 2]
  const mix = (i: number): number =>
    Math.round((from[i] as number) + ((to[i] as number) - (from[i] as number)) * k)
  return [mix(0), mix(1), mix(2), MIN_ALPHA + (MAX_ALPHA - MIN_ALPHA) * v]
}

/** Printed labels need room; below this a cell would show a clipped number. */
const LABEL_MIN_W = 26
const LABEL_MIN_H = 15

// Laws of the game, in metres. Markings only — they are drawn, never measured,
// and no number computed from the data passes through them.
const BOX_DEPTH = 16.5
const BOX_WIDTH = 40.32
const SIX_DEPTH = 5.5
const SIX_WIDTH = 18.32
const CENTRE_R = 9.15
const SPOT = 11

export function draw(canvas: HTMLCanvasElement, o: PitchOptions): HitTest {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => null

  const ext = extent(o)
  const spanX = ext.x.max - ext.x.min
  const spanY = ext.y.max - ext.y.min
  const dpr = window.devicePixelRatio || 1
  const w = canvas.clientWidth || 640
  const h = Math.round((w * spanY) / spanX)

  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  canvas.style.height = `${h}px`
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, w, h)

  const px = (v: number): number => ((v - ext.x.min) / spanX) * w
  // Flipped: positive y is up the screen, as an analyst draws a pitch.
  const py = (v: number): number => ((ext.y.max - v) / spanY) * h

  // Off-pitch area, so a bin outside the touchline reads as outside.
  ctx.fillStyle = OUTSIDE
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = 'rgba(0,0,0,0)'
  ctx.clearRect(px(o.pitchX.min), py(o.pitchY.max),
    px(o.pitchX.max) - px(o.pitchX.min), py(o.pitchY.min) - py(o.pitchY.max))

  // Kept so the pointer can be answered without redoing this arithmetic.
  const rects: Array<{ cell: PitchCell; x: number; y: number; w: number; h: number }> = []

  const peak = o.cells.reduce((m, c) => Math.max(m, c.value), 0)
  for (const c of o.cells) {
    const x0 = px(c.x)
    const y0 = py(c.y + o.binY)
    const cw = px(c.x + o.binX) - x0
    const ch = py(c.y) - y0
    rects.push({ cell: c, x: x0, y: y0, w: cw, h: ch })
    if (peak <= 0) continue
    const [r, g, b, a] = ramp(c.value / peak)
    ctx.fillStyle = `rgba(${r},${g},${b},${a.toFixed(3)})`
    ctx.fillRect(x0, y0, cw, ch)
  }

  ctx.strokeStyle = LINE
  ctx.lineWidth = 1
  const midY = (o.pitchY.min + o.pitchY.max) / 2
  const midX = (o.pitchX.min + o.pitchX.max) / 2

  const box = (side: 1 | -1, depth: number, width: number): void => {
    const goalLine = side === 1 ? o.pitchX.max : o.pitchX.min
    const inner = goalLine - side * depth
    ctx.strokeRect(
      Math.min(px(goalLine), px(inner)), py(midY + width / 2),
      Math.abs(px(inner) - px(goalLine)), py(midY - width / 2) - py(midY + width / 2),
    )
  }

  ctx.strokeRect(px(o.pitchX.min), py(o.pitchY.max),
    px(o.pitchX.max) - px(o.pitchX.min), py(o.pitchY.min) - py(o.pitchY.max))

  ctx.beginPath()
  ctx.moveTo(px(midX), py(o.pitchY.min))
  ctx.lineTo(px(midX), py(o.pitchY.max))
  ctx.stroke()

  ctx.beginPath()
  ctx.ellipse(px(midX), py(midY), px(midX + CENTRE_R) - px(midX),
    py(midY) - py(midY + CENTRE_R), 0, 0, Math.PI * 2)
  ctx.stroke()

  for (const side of [1, -1] as const) {
    box(side, BOX_DEPTH, BOX_WIDTH)
    box(side, SIX_DEPTH, SIX_WIDTH)
    const spot = side === 1 ? o.pitchX.max - SPOT : o.pitchX.min + SPOT
    ctx.beginPath()
    ctx.arc(px(spot), py(midY), 1.5, 0, Math.PI * 2)
    ctx.fillStyle = LINE
    ctx.fill()
  }

  // Values last, so a line never crosses a digit. Light text throughout: even
  // the palest cell is translucent over a dark surface, so there is no point
  // on the ramp where dark text would be the readable choice.
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = '600 11px system-ui, -apple-system, sans-serif'
  for (const { cell, x, y, w: cw, h: ch } of rects) {
    if (!cell.text || cw < LABEL_MIN_W || ch < LABEL_MIN_H) continue
    // A halo rather than a backing box: the box would hide the marking the
    // translucent fill was chosen to preserve.
    ctx.lineWidth = 3
    ctx.strokeStyle = 'rgba(6,10,18,0.85)'
    ctx.strokeText(cell.text, x + cw / 2, y + ch / 2)
    ctx.fillStyle = 'rgba(248,250,252,0.96)'
    ctx.fillText(cell.text, x + cw / 2, y + ch / 2)
  }
  ctx.lineWidth = 1

  return (offsetX, offsetY) => {
    for (const r of rects) {
      if (offsetX >= r.x && offsetX < r.x + r.w && offsetY >= r.y && offsetY < r.y + r.h) {
        return r.cell
      }
    }
    return null
  }
}
